// Cloud Migration two-level taxonomy — DISPLAY-ONLY derivation shared across
// the Evaluation Insights, Recommendations and Implementation tabs so wording
// stays identical everywhere. No scoring / recommendation-selection logic
// lives here; it maps the approved migration-approach label into a
// Transformation Strategy (+ optional Migration Approach) and back.

export const CM_STRATEGY_KEYS = [
  "technical_migration",
  "selective_migration",
  "full_transformation",
];

export const CM_STRATEGY_LABELS = {
  technical_migration: "Technical Migration",
  selective_migration: "Selective Migration",
  full_transformation: "Full Transformation",
};

export const CM_STRATEGY_DESCRIPTIONS = {
  technical_migration:
    "Technical migration to SAP S/4HANA Private Cloud while preserving existing warehouse operations and minimizing business disruption.",
  selective_migration:
    "Business improvements combined with technical migration while maintaining controlled scope.",
  full_transformation:
    "End-to-end business and technology transformation including process redesign and modernization.",
};

// Migration Approach applies only to the Technical Migration strategy.
export const CM_APPROACH_LABELS = ["Lift & Shift", "Lift, Shift & Validate"];

export const CM_APPROACH_DESCRIPTIONS = {
  "Lift & Shift":
    "Technical migration with minimal functional change — fastest path to the target platform.",
  "Lift, Shift & Validate":
    "Technical migration plus full functional, integration and regression validation before go-live.",
};

// Implementation template solution_type per strategy (matches the admin
// implementation_templates transformation_path grouping).
export const CM_STRATEGY_TEMPLATE_KEYS = {
  technical_migration: ["cm_lift_shift", "cm_lift_shift_validate"],
  selective_migration: ["selective_migration"],
  full_transformation: ["full_transformation"],
};

export const CM_STRATEGY_TRANSFORMATION_PATH = {
  technical_migration: "technical_migration",
  selective_migration: "selective_migration",
  full_transformation: "full_transformation",
};

// Approved approach / stored solution label → { strategyKey, approach }.
// `approach` is only meaningful for Technical Migration.
export function deriveCmStrategy(label) {
  const a = String(label || "").toLowerCase();
  if (a.includes("full transformation")) {
    return { strategyKey: "full_transformation", approach: null };
  }
  if (a.includes("selective")) {
    return { strategyKey: "selective_migration", approach: null };
  }
  if (a.includes("validate")) {
    return { strategyKey: "technical_migration", approach: "Lift, Shift & Validate" };
  }
  // Any remaining lift/technical variant defaults to Lift & Shift.
  return { strategyKey: "technical_migration", approach: "Lift & Shift" };
}

// Two-level selection → the single stored `solution` leaf label the override
// framework persists (must be one of the library-driven allowed_solutions).
export function toCmSolutionLabel(strategyKey, approach) {
  if (strategyKey === "selective_migration") return "Selective Modernization";
  if (strategyKey === "full_transformation") return "Full Transformation";
  return approach === "Lift, Shift & Validate"
    ? "Lift, Shift & Validate"
    : "Lift & Shift Migration";
}

// Implementation template solution_type for a resolved two-level selection.
export function cmTemplateKeyFor(strategyKey, approach) {
  if (strategyKey === "selective_migration") return "selective_migration";
  if (strategyKey === "full_transformation") return "full_transformation";
  return approach === "Lift, Shift & Validate"
    ? "cm_lift_shift_validate"
    : "cm_lift_shift";
}

// Implementation template solution_type → transformation strategy key.
export function cmStrategyFromTemplateKey(key) {
  if (key === "selective_migration") return "selective_migration";
  if (key === "full_transformation") return "full_transformation";
  return "technical_migration";
}
